import { InputControl, TextareaControl } from "@shared/components/form";
import { MediaUploader } from "@shared/components/media/MediaUploader";
import ModalBlank from "@shared/components/modals/ModalBlank";
import { Button } from "@shared/components/ui/Button";
import { MediaFile, MediaType } from "@shared/types/media.types";
import { toast } from "react-hot-toast";
import React, { useState } from "react";
import { QuoteSectionData } from "../../types/home.types";

// Form values interface
interface FormValues {
  quote: string;
  person: {
    name: string;
    role: string;
    company: string;
    imagePath: string;
  };
}

interface EditQuoteSectionModalProps {
  initialData: QuoteSectionData;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<QuoteSectionData>) => Promise<void>;
  isLoading?: boolean;
}

const EditQuoteSectionModal: React.FC<EditQuoteSectionModalProps> = ({
  initialData,
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  // Initialize form state
  const [formValues, setFormValues] = useState<FormValues>({
    quote: initialData?.quote || "",
    person: {
      name: initialData?.person?.name || "",
      role: initialData?.person?.role || "",
      company: initialData?.person?.company || "",
      imagePath: initialData?.person?.imagePath || "",
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Media upload state
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>(
    initialData?.person?.imagePath
      ? [
          {
            type: MediaType.IMAGE,
            preview: initialData.person.imagePath,
            uploadedUrl: initialData.person.imagePath,
            file: new File([], "", { type: "image/jpeg" }),
            progress: 100,
          },
        ]
      : []
  );

  // Handle input changes
  const handleChange = (field: keyof FormValues | string, value: string) => {
    // Handle nested person fields
    if (field.startsWith('person.')) {
      const personField = field.split('.')[1] as keyof FormValues['person'];
      setFormValues((prev) => ({
        ...prev,
        person: {
          ...prev.person,
          [personField]: value,
        },
      }));

      // Clear error for this field when user types
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    } else {
      // Handle top-level fields
      setFormValues((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear error for this field when user types
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    }
  };

  // Handle media upload
  const handleMediaUpload = (newMediaFiles: MediaFile[]) => {
    if (newMediaFiles.length > 0) {
      const uploadedUrl = newMediaFiles[0].uploadedUrl || newMediaFiles[0].preview;
      setMediaFiles(newMediaFiles);
      setFormValues((prev) => ({
        ...prev,
        person: {
          ...prev.person,
          imagePath: uploadedUrl,
        },
      }));
      
      // Clear error for imagePath if it exists
      if (errors["person.imagePath"]) {
        setErrors((prev) => ({
          ...prev,
          "person.imagePath": "",
        }));
      }
    }
  };

  // Handle removing an image
  const handleRemoveImage = () => {
    setMediaFiles([]);
    setFormValues((prev) => ({
      ...prev,
      person: {
        ...prev.person,
        imagePath: "",
      },
    }));
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formValues.quote.trim()) {
      newErrors.quote = "Quote is required";
    }

    if (!formValues.person.name.trim()) {
      newErrors["person.name"] = "Name is required";
    }

    if (!formValues.person.role.trim()) {
      newErrors["person.role"] = "Role is required";
    }

    if (!formValues.person.company.trim()) {
      newErrors["person.company"] = "Company is required";
    }

    if (!formValues.person.imagePath.trim()) {
      newErrors["person.imagePath"] = "Person image is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formValues);
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <ModalBlank
      id="edit-quote-section-modal"
      modalOpen={isOpen}
      setModalOpen={onClose}
      showCloseButton={true}
      className="max-w-[500px] p-6"
    >
      <div className="mb-4">
        <h2 className="text-lg font-semibold">
          Edit Quote Section
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <TextareaControl
          id="quote"
          label="Quote"
          value={formValues.quote}
          onChange={(e) => handleChange("quote", e.target.value)}
          placeholder="Enter quote"
          disabled={isLoading}
          error={errors.quote}
          required
        />

        <InputControl
          id="person-name"
          label="Person Name"
          value={formValues.person.name}
          onChange={(e) => handleChange("person.name", e.target.value)}
          placeholder="Enter person name"
          disabled={isLoading}
          error={errors["person.name"]}
          required
        />

        <InputControl
          id="person-role"
          label="Person Role"
          value={formValues.person.role}
          onChange={(e) => handleChange("person.role", e.target.value)}
          placeholder="Enter person role"
          disabled={isLoading}
          error={errors["person.role"]}
          required
        />

        <InputControl
          id="person-company"
          label="Person Company"
          value={formValues.person.company}
          onChange={(e) => handleChange("person.company", e.target.value)}
          placeholder="Enter person company"
          disabled={isLoading}
          error={errors["person.company"]}
          required
        />

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Person Image
            {errors["person.imagePath"] && (
              <span className="text-red-500 ml-1">{errors["person.imagePath"]}</span>
            )}
          </label>

          {/* Media Uploader if no image */}
          {mediaFiles.length === 0 && (
            <div className="mb-3">
              <MediaUploader
                onUploadComplete={handleMediaUpload}
                onError={(error) => {
                  toast.error(error);
                }}
                maxFiles={1}
                acceptType="image"
              />
            </div>
          )}

          {/* Media preview */}
          {mediaFiles.length > 0 && (
            <div className="relative rounded-md overflow-hidden bg-gray-100 dark:bg-gray-700 mb-3">
              <img
                src={formValues.person.imagePath}
                alt="Person"
                className="w-full h-auto object-cover max-h-[200px]"
              />
              <button
                type="button"
                className="absolute top-2 right-2 bg-gray-800 bg-opacity-60 rounded-full p-1 text-white"
                onClick={handleRemoveImage}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </ModalBlank>
  );
};

export default EditQuoteSectionModal;
